import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Mail, Shield, Receipt, Send, Users, Pencil, BarChart3, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import PlanSwitcher from "@/components/PlanSwitcher";
import { SIGNATURE_TOKENS } from "@/lib/placeholders";

const MONTHLY_PAID_PLANS = ["plan_starter", "plan_growth", "plan_scale"];

function fmtMoney(amountCents, currency) {
  if (amountCents == null) return "—";
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format(amountCents / 100);
  } catch {
    return `$${(amountCents / 100).toFixed(2)}`;
  }
}

function statusPill(s) {
  const map = {
    paid: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    pending: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    failed: "bg-red-500/10 text-red-400 border-red-500/20",
    expired: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
    refunded: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  };
  return `text-xs px-2 py-0.5 rounded border ${map[s] || "border-border text-muted-foreground"}`;
}

export default function Account() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [txs, setTxs] = useState(null);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [sendingClientPreview, setSendingClientPreview] = useState(false);
  const [prefBusy, setPrefBusy] = useState(false);
  const [clientEmailInput, setClientEmailInput] = useState("");
  const [savingClientEmail, setSavingClientEmail] = useState(false);
  const [signatureInput, setSignatureInput] = useState("");
  const [savingSignature, setSavingSignature] = useState(false);
  const [digests, setDigests] = useState(null);
  const [insights, setInsights] = useState(null);
  const signatureRef = useRef(null);

  useEffect(() => {
    api.get("/me/transactions").then(({ data }) => setTxs(data)).catch(() => setTxs([]));
    api.get("/me/client-digests").then(({ data }) => setDigests(data)).catch(() => setDigests([]));
    api.get("/me/open-insights").then(({ data }) => setInsights(data)).catch(() => setInsights({ by_hour: [], best_hour: null, total_opens: 0, recipients_tracked: 0 }));
  }, []);

  const insertToken = (token) => {
    const el = signatureRef.current;
    if (!el) {
      setSignatureInput((s) => s + token);
      return;
    }
    const start = el.selectionStart ?? signatureInput.length;
    const end = el.selectionEnd ?? signatureInput.length;
    const next = signatureInput.slice(0, start) + token + signatureInput.slice(end);
    setSignatureInput(next);
    // Restore caret after React re-renders
    requestAnimationFrame(() => {
      if (signatureRef.current) {
        const pos = start + token.length;
        signatureRef.current.focus();
        signatureRef.current.setSelectionRange(pos, pos);
      }
    });
  };

  useEffect(() => {
    if (user) {
      setClientEmailInput(user.client_email_default || "");
      setSignatureInput(user.email_signature || "");
    }
  }, [user?.client_email_default, user?.email_signature]);

  const isMonthlyPaid =
    MONTHLY_PAID_PLANS.includes(user?.plan) &&
    user?.subscription_interval === "month" &&
    ["active", "trialing"].includes(user?.subscription_status);

  const togglePref = async (key, checked) => {
    setPrefBusy(true);
    try {
      await api.patch("/me/preferences", { [key]: checked });
      await refresh();
      toast.success(checked ? "Turned on" : "Turned off");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setPrefBusy(false);
    }
  };

  const saveClientEmail = async () => {
    setSavingClientEmail(true);
    try {
      await api.patch("/me/preferences", { client_email_default: clientEmailInput.trim() });
      await refresh();
      toast.success(clientEmailInput.trim() ? "Default client email saved" : "Default client email cleared");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSavingClientEmail(false);
    }
  };

  const sendPreview = async () => {
    setSendingPreview(true);
    try {
      await api.post("/me/weekly-report/send-now");
      toast.success("Preview email sent — check your inbox");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSendingPreview(false);
    }
  };

  const sendClientPreview = async () => {
    setSendingClientPreview(true);
    try {
      await api.post("/me/weekly-client-email/send-now");
      const to = (user?.client_email_default || user?.email || "").trim() || "your inbox";
      toast.success(`Preview sent to ${to}`);
      // Refresh analytics so the new digest row appears
      try {
        const { data } = await api.get("/me/client-digests");
        setDigests(data);
      } catch { /* ignore */ }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Send failed");
    } finally {
      setSendingClientPreview(false);
    }
  };

  const isPaid = user && user.plan && user.plan !== "free";

  const saveSignature = async () => {
    setSavingSignature(true);
    try {
      await api.patch("/me/preferences", { email_signature: signatureInput });
      await refresh();
      toast.success(signatureInput.trim() ? "Signature saved" : "Signature cleared");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSavingSignature(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Nav />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="overline text-primary">Account</div>
        <h1 className="mt-2 text-4xl font-medium tracking-tighter">Your account</h1>

        <div className="mt-10 grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-border bg-[#121214] p-6" data-testid="account-email-card">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <div className="mt-4 text-xs text-muted-foreground uppercase tracking-[0.18em]">Email</div>
            <div className="mt-1 text-sm break-all" data-testid="account-email">{user?.email}</div>
          </div>

          <div className="rounded-xl border border-border bg-[#121214] p-6" data-testid="account-credits-card">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-primary" />
            </div>
            <div className="mt-4 text-xs text-muted-foreground uppercase tracking-[0.18em]">Credits</div>
            <div className="mt-1 text-2xl font-medium" data-testid="account-credits">
              {user?.credits === -1 ? "Unlimited" : user?.credits ?? 0}
            </div>
            {user?.credits !== -1 && (
              <Button asChild size="sm" variant="outline" className="mt-3 rounded-full h-8 border-primary/40 text-primary hover:bg-primary/10" data-testid="account-buy-credits">
                <Link to="/pricing">{user?.plan === "free" ? "Upgrade plan" : "Change plan"}</Link>
              </Button>
            )}
          </div>

          <div className="rounded-xl border border-border bg-[#121214] p-6" data-testid="account-plan-card">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary" />
            </div>
            <div className="mt-4 text-xs text-muted-foreground uppercase tracking-[0.18em]">Plan</div>
            <div className="mt-1 text-sm capitalize" data-testid="account-plan">{user?.plan}</div>
          </div>
        </div>

        {/* Plan switcher */}
        <PlanSwitcher />

        {/* Email preferences */}
        <section className="mt-12">
          <div className="overline text-primary flex items-center gap-2">
            <Send className="w-3.5 h-3.5" /> Email preferences
          </div>
          <h2 className="mt-2 text-xl font-medium tracking-tight">Notifications</h2>
          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-border bg-[#121214] p-6 flex items-start justify-between gap-6" data-testid="weekly-report-card">
              <div className="max-w-xl">
                <div className="font-medium">Monday weekly report</div>
                <div className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  Every Monday at 09:00 UTC — new lead lists & leads, your pipeline moves in the past 7 days, and your top 5 new leads by fit score.
                </div>
              </div>
              <div className="flex flex-col items-end gap-3">
                <Switch
                  checked={!!user?.weekly_email}
                  onCheckedChange={(v) => togglePref("weekly_email", v)}
                  disabled={prefBusy}
                  data-testid="weekly-email-switch"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={sendingPreview}
                  onClick={sendPreview}
                  data-testid="send-weekly-preview-btn"
                >
                  {sendingPreview ? "Sending…" : "Send preview now"}
                </Button>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-[#121214] p-6 flex items-start justify-between gap-6" data-testid="reply-alerts-card">
              <div className="max-w-xl">
                <div className="font-medium">Smart reply alerts</div>
                <div className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  Get an instant email the moment you flip a lead to <span className="text-foreground">Replied</span> so you can follow up inside the golden hour.
                </div>
              </div>
              <Switch
                checked={!!user?.reply_alerts}
                onCheckedChange={(v) => togglePref("reply_alerts", v)}
                disabled={prefBusy}
                data-testid="reply-alerts-switch"
              />
            </div>

            <div
              className={`rounded-xl border bg-[#121214] p-6 ${
                isMonthlyPaid ? "border-primary/30" : "border-border opacity-80"
              }`}
              data-testid="weekly-client-email-card"
            >
              <div className="flex items-start justify-between gap-6">
                <div className="max-w-xl">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    <div className="font-medium">Monday client digest</div>
                    {isMonthlyPaid ? (
                      <span className="text-[10px] uppercase tracking-[0.14em] text-primary bg-primary/10 border border-primary/30 rounded-full px-2 py-0.5" data-testid="client-digest-eligible-pill">
                        Included in your plan
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border border-border rounded-full px-2 py-0.5" data-testid="client-digest-upgrade-pill">
                        Monthly plans only
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    Every Monday at 09:00 UTC, we email your client the week's new lead lists, top 10 leads by fit score, and a summary — straight to their inbox, with your email in the reply-to field.
                  </div>
                </div>
                <Switch
                  checked={!!user?.client_weekly_email}
                  onCheckedChange={(v) => togglePref("client_weekly_email", v)}
                  disabled={prefBusy || !isMonthlyPaid}
                  data-testid="client-weekly-email-switch"
                />
              </div>

              <div className="mt-5 border-t border-border pt-5 space-y-3">
                <div>
                  <Label htmlFor="client_email_default" className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                    Default client email
                  </Label>
                  <div className="mt-2 flex items-stretch gap-2">
                    <Input
                      id="client_email_default"
                      type="email"
                      placeholder="client@theircompany.com"
                      value={clientEmailInput}
                      onChange={(e) => setClientEmailInput(e.target.value)}
                      className="flex-1"
                      data-testid="client-email-default-input"
                      disabled={!isMonthlyPaid && !user?.client_email_default}
                    />
                    <Button
                      variant="outline"
                      className="rounded-full"
                      onClick={saveClientEmail}
                      disabled={savingClientEmail || clientEmailInput === (user?.client_email_default || "")}
                      data-testid="client-email-default-save"
                    >
                      {savingClientEmail ? "Saving…" : "Save"}
                    </Button>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    You can override this per lead list when generating. Leave blank to disable the default.
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={sendClientPreview}
                    disabled={sendingClientPreview}
                    data-testid="send-client-digest-preview-btn"
                  >
                    {sendingClientPreview ? "Sending…" : "Send preview now"}
                  </Button>
                  {!isMonthlyPaid && (
                    <Button asChild variant="link" size="sm" className="text-primary px-0" data-testid="client-digest-upgrade-link">
                      <Link to="/pricing">Upgrade to a monthly plan →</Link>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Email signature */}
        <section className="mt-12">
          <div className="overline text-primary flex items-center gap-2">
            <Pencil className="w-3.5 h-3.5" /> Email signature
          </div>
          <h2 className="mt-2 text-xl font-medium tracking-tight">Reusable signature</h2>
          <div
            className={`mt-4 rounded-xl border bg-[#121214] p-6 ${
              isPaid ? "border-primary/30" : "border-border"
            }`}
            data-testid="email-signature-card"
          >
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="max-w-xl">
                <div className="flex items-center gap-2">
                  <div className="font-medium">Signature block</div>
                  {isPaid ? (
                    <span className="text-[10px] uppercase tracking-[0.14em] text-primary bg-primary/10 border border-primary/30 rounded-full px-2 py-0.5" data-testid="signature-paid-pill">
                      Paid feature
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border border-border rounded-full px-2 py-0.5" data-testid="signature-upgrade-pill">
                      Upgrade required
                    </span>
                  )}
                </div>
                <div className="mt-1 text-sm text-muted-foreground leading-relaxed">
                  Set it once — your signature auto-appears at the bottom of every lead's outreach draft and in every email we send on your behalf (client digests, email-to-client).
                </div>
              </div>
            </div>
            <div className="mt-5 space-y-3">
              {isPaid && (
                <div className="flex flex-wrap gap-1.5" data-testid="signature-token-chips">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mr-2 self-center">Insert:</span>
                  {SIGNATURE_TOKENS.map((t) => (
                    <button
                      type="button"
                      key={t.token}
                      onClick={() => insertToken(t.token)}
                      className="text-[11px] font-mono px-2 py-1 rounded-full border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
                      data-testid={`signature-token-${t.token.replace(/[^a-z_]/gi, "")}`}
                      title={`Insert ${t.label}`}
                    >
                      {t.token}
                    </button>
                  ))}
                </div>
              )}
              <Textarea
                ref={signatureRef}
                value={signatureInput}
                onChange={(e) => setSignatureInput(e.target.value)}
                rows={5}
                maxLength={1000}
                placeholder={isPaid
                  ? "Hey {{first_name}},\n— Jane Doe, Founder at Acme Co.\njane@acme.com"
                  : "Upgrade to a paid plan to save a signature."}
                className="font-sans"
                disabled={!isPaid}
                data-testid="email-signature-textarea"
              />
              <div className="text-[11px] text-muted-foreground leading-relaxed">
                Variables like <code className="text-primary">{"{{company}}"}</code> and <code className="text-primary">{"{{first_name}}"}</code> auto-fill with each lead's data when you copy a draft. In generic emails (Monday digest, Email to Client) unresolved variables are stripped automatically.
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-muted-foreground mono" data-testid="signature-char-count">
                  {signatureInput.length} / 1000
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {!isPaid && (
                    <Button asChild variant="link" size="sm" className="text-primary px-0" data-testid="signature-upgrade-link">
                      <Link to="/pricing">Upgrade plan →</Link>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    className="rounded-full"
                    onClick={saveSignature}
                    disabled={savingSignature || signatureInput === (user?.email_signature || "") || (!isPaid && signatureInput.trim().length > 0)}
                    data-testid="signature-save-btn"
                  >
                    {savingSignature ? "Saving…" : "Save signature"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Client digest analytics */}
        <section className="mt-12">
          <div className="overline text-primary flex items-center gap-2">
            <BarChart3 className="w-3.5 h-3.5" /> Client digest analytics
          </div>
          <h2 className="mt-2 text-xl font-medium tracking-tight">Monday digest performance</h2>

          {insights && insights.total_opens > 0 && (
            <div className="mt-4 rounded-xl border border-border bg-[#121214] p-6" data-testid="open-heatmap-card">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-muted-foreground">
                    <Clock className="w-3.5 h-3.5 text-primary" /> Reply-window heatmap
                  </div>
                  <h3 className="mt-1 text-base font-medium">When your clients open your emails</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Hour-of-day distribution across {insights.recipients_tracked} tracked recipient{insights.recipients_tracked === 1 ? "" : "s"} · {insights.total_opens} total opens
                  </p>
                </div>
                {insights.best_hour !== null && insights.best_hour !== undefined && (
                  <div className="text-right" data-testid="heatmap-best-hour-label">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Best send hour</div>
                    <div className="font-mono text-2xl text-primary">{String(insights.best_hour).padStart(2, "0")}:00</div>
                    <div className="text-[10px] text-muted-foreground">UTC</div>
                  </div>
                )}
              </div>
              <div className="flex items-end gap-[3px] h-28" data-testid="heatmap-bars">
                {(insights.by_hour || Array(24).fill(0)).map((count, hour) => {
                  const max = Math.max(1, ...(insights.by_hour || []));
                  const pct = Math.round((count / max) * 100);
                  const isBest = hour === insights.best_hour;
                  return (
                    <div
                      key={hour}
                      className="flex-1 flex flex-col items-center justify-end group"
                      data-testid={`heatmap-hour-${hour}`}
                      title={`${String(hour).padStart(2, "0")}:00 UTC · ${count} open${count === 1 ? "" : "s"}`}
                    >
                      <div
                        className={`w-full rounded-t transition-colors ${
                          isBest
                            ? "bg-primary"
                            : count > 0
                            ? "bg-primary/30 group-hover:bg-primary/50"
                            : "bg-border/40"
                        }`}
                        style={{ height: `${count > 0 ? Math.max(6, pct) : 2}%` }}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-muted-foreground mono">
                <span>00</span>
                <span>06</span>
                <span>12</span>
                <span>18</span>
                <span>23</span>
              </div>
            </div>
          )}

          <div className="mt-4 rounded-xl border border-border bg-[#121214] overflow-hidden" data-testid="digest-analytics-card">
            {digests === null ? (
              <div className="p-6 text-sm text-muted-foreground">Loading…</div>
            ) : digests.length === 0 ? (
              <div className="p-10 text-center">
                <div className="text-sm text-muted-foreground">
                  No client digests sent yet. Send a preview to see opens appear here.
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-5 rounded-full"
                  onClick={sendClientPreview}
                  disabled={sendingClientPreview}
                  data-testid="analytics-send-preview-btn"
                >
                  {sendingClientPreview ? "Sending…" : "Send preview now"}
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Sent</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead className="text-center">Leads</TableHead>
                    <TableHead>Opened</TableHead>
                    <TableHead className="text-center">Opens</TableHead>
                    <TableHead className="text-center">Clicks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {digests.map((d) => {
                    const opened = !!d.opened_at;
                    const clickCount = d.total_clicks ?? (d.clicks?.length ?? 0);
                    const topClick = (d.clicks || [])
                      .reduce((acc, c) => {
                        acc[c.lead_id] = (acc[c.lead_id] || { company: c.company, count: 0 });
                        acc[c.lead_id].count += 1;
                        return acc;
                      }, {});
                    const topClickEntry = Object.values(topClick).sort((a, b) => b.count - a.count)[0];
                    return (
                      <TableRow key={d.id} data-testid={`digest-row-${d.id}`}>
                        <TableCell className="text-xs text-muted-foreground mono">
                          {new Date(d.sent_at).toLocaleString()}
                          {d.scheduled_for && d.status === "scheduled" && (
                            <div className="mt-0.5 text-[10px] text-primary">
                              Smart-send → {new Date(d.scheduled_for).toLocaleString()}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{d.recipient}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {d.business_name || "—"}
                          {d.kind === "preview" && (
                            <span className="ml-2 text-[10px] uppercase tracking-[0.14em] text-muted-foreground border border-border rounded-full px-1.5 py-0.5">
                              preview
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-center mono text-sm">{d.new_leads ?? 0}</TableCell>
                        <TableCell>
                          {opened ? (
                            <span className="text-xs px-2 py-0.5 rounded border bg-emerald-500/10 text-emerald-400 border-emerald-500/20" data-testid={`digest-opened-${d.id}`}>
                              {new Date(d.opened_at).toLocaleString()}
                            </span>
                          ) : d.status === "failed" ? (
                            <span className="text-xs px-2 py-0.5 rounded border bg-red-500/10 text-red-400 border-red-500/20">
                              failed
                            </span>
                          ) : d.status === "scheduled" ? (
                            <span className="text-xs px-2 py-0.5 rounded border bg-amber-500/10 text-amber-400 border-amber-500/20" data-testid={`digest-scheduled-${d.id}`}>
                              scheduled
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center mono text-sm">{d.open_count ?? 0}</TableCell>
                        <TableCell className="text-center mono text-sm" data-testid={`digest-clicks-${d.id}`}>
                          {clickCount || 0}
                          {topClickEntry && (
                            <div className="mt-0.5 text-[10px] text-muted-foreground truncate max-w-[12ch] mx-auto" title={topClickEntry.company}>
                              {topClickEntry.company}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </section>


        {/* Billing history */}
        <section className="mt-12">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <div className="overline text-primary flex items-center gap-2">
                <Receipt className="w-3.5 h-3.5" /> Billing history
              </div>
              <h2 className="mt-2 text-xl font-medium tracking-tight">Past purchases</h2>
            </div>
            <Button asChild variant="outline" className="rounded-full" data-testid="billing-pricing-link">
              <Link to="/pricing">View packs</Link>
            </Button>
          </div>

          <div className="rounded-xl border border-border bg-[#121214] overflow-hidden">
            {txs === null ? (
              <div className="p-6 text-sm text-muted-foreground">Loading…</div>
            ) : txs.length === 0 ? (
              <div className="p-10 text-center">
                <div className="text-sm text-muted-foreground">No purchases yet.</div>
                <Button asChild className="mt-5 rounded-full bg-primary hover:bg-[#FF7043] text-white" data-testid="empty-buy-link">
                  <Link to="/pricing">Browse credit packs</Link>
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Date</TableHead>
                    <TableHead>Pack</TableHead>
                    <TableHead>Credits</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {txs.map((t) => (
                    <TableRow key={t.session_id} data-testid={`tx-row-${t.session_id}`}>
                      <TableCell className="text-xs text-muted-foreground mono">
                        {new Date(t.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm capitalize">
                        {t.lookup_key?.replace("credits_", "")}
                      </TableCell>
                      <TableCell className="mono">
                        {t.credits_granted ? `+${t.credits}` : t.credits}
                      </TableCell>
                      <TableCell className="mono">{fmtMoney(t.amount, t.currency)}</TableCell>
                      <TableCell><span className={statusPill(t.payment_status)}>{t.payment_status}</span></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </section>

        <div className="mt-12">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => { logout(); nav("/"); }}
            data-testid="account-logout-btn"
          >
            Log out
          </Button>
        </div>
      </main>
    </div>
  );
}
